import { useMemo, useState } from 'react';
import { Card, ConfirmModal } from '@safetyhub/ui';
import { PERMISSION_GROUP_LABELS, groupPermissions } from '@safetyhub/shared';
import type { PermissionDefinitionView } from '../../lib/company-rbac';

interface PermissionDefinitionsSectionProps {
  permissions: PermissionDefinitionView[];
  /** 토글 진행 중인 key(버튼 잠금) */
  busyKey: string | null;
  onToggle: (key: string, orgAdjustable: boolean) => Promise<void>;
}

/**
 * ③ 권한 정의 표 — 22키(group별)·라벨·설명·`orgAdjustable` 토글. 정본은 DB 컬럼(코드 값은 최초 기본값)이라 토글이
 * 판정·조직 편집 API에 즉시 반영된다. **끄는** 방향만 확인 모달을 거친다 — 조직 오버라이드가 켜 둔 값이 그 순간부터
 * 무시되는(시스템 값으로 고정) 되돌리기 쉬운-그러나-눈에 안 보이는 효과이기 때문. 켜는 방향은 즉시.
 */
export default function PermissionDefinitionsSection({ permissions, busyKey, onToggle }: PermissionDefinitionsSectionProps) {
  const groups = useMemo(() => groupPermissions(permissions), [permissions]);
  const [pendingOff, setPendingOff] = useState<PermissionDefinitionView | null>(null);
  const [confirming, setConfirming] = useState(false);

  const handleClick = (p: PermissionDefinitionView) => {
    if (busyKey) return;
    if (p.orgAdjustable) setPendingOff(p);
    else void onToggle(p.key, true);
  };

  const handleConfirmOff = async () => {
    if (!pendingOff) return;
    setConfirming(true);
    try {
      await onToggle(pendingOff.key, false);
      setPendingOff(null);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Card padding="none" radius="lg" className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm" data-testid="permission-definitions">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">권한</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">key</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">설명</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap">
              조직 조정
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {groups.map(({ group, items }) => (
            <GroupRows key={group} group={group} items={items} busyKey={busyKey} onClick={handleClick} />
          ))}
        </tbody>
      </table>

      <ConfirmModal
        open={pendingOff !== null}
        title="조직 조정 끄기"
        body={
          pendingOff
            ? `「${pendingOff.label}」(${pendingOff.key})을 끄면 조직 오버라이드·조직 신규 역할에서 이 키는 시스템 역할 값으로 고정됩니다. 이미 켜 둔 조직이 있어도 판정에서는 시스템 값이 이깁니다.`
            : undefined
        }
        confirmLabel="끄기"
        danger
        busy={confirming}
        onConfirm={() => void handleConfirmOff()}
        onClose={() => {
          if (!confirming) setPendingOff(null);
        }}
      />
    </Card>
  );
}

function GroupRows({
  group,
  items,
  busyKey,
  onClick,
}: {
  group: string;
  items: PermissionDefinitionView[];
  busyKey: string | null;
  onClick: (p: PermissionDefinitionView) => void;
}) {
  return (
    <>
      <tr className="bg-gray-50/60">
        <th colSpan={4} scope="rowgroup" className="px-4 py-1.5 text-left text-xs font-semibold text-gray-500">
          {PERMISSION_GROUP_LABELS[group] ?? group}
        </th>
      </tr>
      {items.map((p) => (
        <tr key={p.key} data-permission-key={p.key}>
          <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">{p.label}</td>
          <td className="px-4 py-2.5 font-mono text-xs text-gray-600 whitespace-nowrap">{p.key}</td>
          <td className="px-4 py-2.5 text-gray-500">{p.description}</td>
          <td className="px-4 py-2.5 whitespace-nowrap">
            <button
              type="button"
              role="switch"
              aria-checked={p.orgAdjustable}
              aria-label={`${p.label} 조직 조정`}
              disabled={busyKey !== null}
              onClick={() => onClick(p)}
              className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium ring-1 transition-colors disabled:opacity-50 ${
                p.orgAdjustable
                  ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100'
                  : 'bg-gray-100 text-gray-600 ring-gray-200 hover:bg-gray-200'
              }`}
            >
              <span aria-hidden="true">{p.orgAdjustable ? '✓' : '🔒'}</span>
              {p.orgAdjustable ? '조정 가능' : '시스템 고정'}
            </button>
          </td>
        </tr>
      ))}
    </>
  );
}
